import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook for tracking user inactivity and triggering a timeout callback.
 *
 * @param {number} idleTimeoutMs - Timeout in ms (default: 30 minutes = 1800000).
 * @param {number} warningTimeMs - Warning time in ms before timeout (default: 5 minutes = 300000).
 * @param {Function} onTimeout - Callback to invoke when the timeout is reached.
 * @returns {{ warningVisible: boolean, resetIdleTimer: Function }}
 */
export default function useIdleTimeout(idleTimeoutMs = 1800000, warningTimeMs = 300000, onTimeout) {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const [warningVisible, setWarningVisible] = useState(false);
  const onTimeoutRef = useRef(onTimeout);

  // Keep the callback ref up to date
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const resetIdleTimer = useCallback(() => {
    // Clear any existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    setWarningVisible(false);

    // Set warning timer
    if (warningTimeMs > 0) {
      warningRef.current = setTimeout(() => {
        setWarningVisible(true);
      }, idleTimeoutMs - warningTimeMs);
    }

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      setWarningVisible(false);
      if (onTimeoutRef.current) {
        onTimeoutRef.current();
      }
    }, idleTimeoutMs);
  }, [idleTimeoutMs, warningTimeMs]);

  useEffect(() => {
    resetIdleTimer();

    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'mouseup'];
    const handleActivity = () => {
      resetIdleTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetIdleTimer]);

  return { warningVisible, resetIdleTimer };
}
